/**
 * V2.0.47 — ADA Mode helpers.
 *
 * Two pure functions:
 *   - `applyAdaCompliance(blueprint)`  ⟶ bumps every door to its ADA
 *     target width, snapshotting the original on `door._pre_ada_width`
 *     so the user can flip the toggle back off without losing data.
 *   - `revertAdaCompliance(blueprint)` ⟶ restores `door.width` from
 *     `_pre_ada_width` and clears the snapshot fields. Idempotent.
 *
 * The toggle also flips `blueprint.ada_mode` so the field survives
 * persistence (PDF + IFC export read this).
 *
 * Doors:
 *   - Primary bedroom / bathroom / WC / powder doors → 0.914 m (36″).
 *   - All other doors → 0.813 m (32″).
 * If the host wall is too short to accommodate the ADA target the
 * door is flagged in `blocked_doors[]` so the UI can surface a chip;
 * the door's `width` is not bumped above what fits.
 *
 * Windows are intentionally untouched — ADA windows are about sill
 * height + reach hardware, not opening width.
 */

import {
  ADA_DOOR_MIN_M,
  EDGE_MARGIN_M,
  adaDoorTargetWidth,
} from "./openingCodes";

function _findHostWallLength(floor, door) {
  if (!door) return 0;
  if (door.room_a && door.room_b) {
    const ra = (floor.rooms || []).find((r) => r && r.id === door.room_a);
    const rb = (floor.rooms || []).find((r) => r && r.id === door.room_b);
    if (!ra || !rb) return 0;
    // Use shared overlap on either axis.
    const TOL = 0.01;
    const ax1 = ra.x + ra.width, bx1 = rb.x + rb.width;
    const az1 = ra.z + ra.depth, bz1 = rb.z + rb.depth;
    if (Math.abs(ax1 - rb.x) < TOL || Math.abs(ra.x - bx1) < TOL) {
      return Math.max(0, Math.min(az1, bz1) - Math.max(ra.z, rb.z));
    }
    if (Math.abs(az1 - rb.z) < TOL || Math.abs(ra.z - bz1) < TOL) {
      return Math.max(0, Math.min(ax1, bx1) - Math.max(ra.x, rb.x));
    }
    return 0;
  }
  if (door.room_a) {
    const ra = (floor.rooms || []).find((r) => r && r.id === door.room_a);
    if (!ra) return 0;
    const side = String(door.side || "front").toLowerCase();
    return side === "front" || side === "rear" ? Number(ra.width) : Number(ra.depth);
  }
  return 0;
}

function _findHostRoomName(floor, door) {
  if (!door) return null;
  // For interior doors, return whichever side has the more restrictive
  // ADA target (primary bedroom / bath > standard) so the bumped width
  // satisfies the stricter room.
  const ids = [door.room_a, door.room_b].filter(Boolean);
  const names = ids
    .map((id) => (floor.rooms || []).find((rm) => rm && rm.id === id)?.name)
    .filter(Boolean);
  if (names.length === 0) return null;
  // Order names so the primary keyword wins.
  const primary = names.find((n) =>
    ["primary", "master", "bath", "wc", "powder", "accessible"].some((kw) =>
      String(n).toLowerCase().includes(kw),
    ),
  );
  return primary || names[0];
}

/**
 * Returns `{ blueprint, blocked_doors, bumped_doors }`. Pure.
 */
export function applyAdaCompliance(blueprint) {
  if (!blueprint) return { blueprint, blocked_doors: [], bumped_doors: [] };
  const out = JSON.parse(JSON.stringify(blueprint));
  out.ada_mode = true;
  const blocked = [];
  const bumped = [];
  for (const floor of out.floors || []) {
    if (!floor || !Array.isArray(floor.doors)) continue;
    for (const d of floor.doors) {
      if (!d || d.deleted) continue;
      const roomName = _findHostRoomName(floor, d);
      const target = adaDoorTargetWidth(roomName);
      const hostLen = _findHostWallLength(floor, d);
      const available = Math.max(0, hostLen - EDGE_MARGIN_M);
      // Snapshot only on FIRST entry into ADA mode.
      if (!Number.isFinite(d._pre_ada_width)) {
        d._pre_ada_width = Number.isFinite(d.width) ? Number(d.width) : 0.9;
      }
      if (available < ADA_DOOR_MIN_M) {
        blocked.push({ id: d.id || null, room_name: roomName, host_len: hostLen, target });
        continue;
      }
      const finalWidth = Math.min(target, available);
      if (finalWidth > (Number(d.width) || 0)) {
        d.width = Number(finalWidth.toFixed(2));
        bumped.push({ id: d.id || null, room_name: roomName, target: finalWidth, prev: d._pre_ada_width });
      } else {
        // door is already wider than ADA min — leave it alone but still
        // record the snapshot above so revert restores cleanly.
      }
    }
  }
  return { blueprint: out, blocked_doors: blocked, bumped_doors: bumped };
}

/**
 * Restores `door.width` from `_pre_ada_width` and clears the
 * snapshot fields. Idempotent.
 */
export function revertAdaCompliance(blueprint) {
  if (!blueprint) return { blueprint, restored_doors: [] };
  const out = JSON.parse(JSON.stringify(blueprint));
  out.ada_mode = false;
  const restored = [];
  for (const floor of out.floors || []) {
    if (!floor || !Array.isArray(floor.doors)) continue;
    for (const d of floor.doors) {
      if (!d) continue;
      if (Number.isFinite(d._pre_ada_width)) {
        const prev = d.width;
        const next = d._pre_ada_width;
        d.width = next;
        delete d._pre_ada_width;
        if (Math.abs(Number(prev) - Number(next)) > 0.0005) {
          restored.push({ id: d.id || null, from: prev, to: next });
        }
      }
    }
  }
  return { blueprint: out, restored_doors: restored };
}

/**
 * Bumps a SINGLE door to its ADA target width (32″ or 36″ based on
 * the host room name). Snapshots the original on `_pre_ada_width` so
 * the global toggle's revert still restores it.
 *
 * Pure. Returns `{ blueprint, bumped, blocked, target }`.
 *   - `bumped` is the door entry that was updated (or null).
 *   - `blocked` is true if the host wall is too short for ADA min.
 *   - `target` is the ADA target width we tried to hit.
 *
 * Note: this does NOT flip `blueprint.ada_mode` — that bit stays the
 * province of the global toggle. Per-door bumps are an opt-in chip.
 */
export function bumpDoorToAda(blueprint, doorId) {
  if (!blueprint || !doorId) return { blueprint, bumped: null, blocked: false, target: 0 };
  const out = JSON.parse(JSON.stringify(blueprint));
  let bumped = null;
  let blocked = false;
  let target = 0;
  for (const floor of out.floors || []) {
    if (!floor || !Array.isArray(floor.doors)) continue;
    const d = floor.doors.find((x) => x && x.id === doorId);
    if (!d || d.deleted) continue;
    const roomName = _findHostRoomName(floor, d);
    target = adaDoorTargetWidth(roomName);
    const hostLen = _findHostWallLength(floor, d);
    const available = Math.max(0, hostLen - EDGE_MARGIN_M);
    if (available < ADA_DOOR_MIN_M) {
      blocked = true;
      break;
    }
    if (!Number.isFinite(d._pre_ada_width)) {
      d._pre_ada_width = Number.isFinite(d.width) ? Number(d.width) : 0.9;
    }
    const finalWidth = Math.min(target, available);
    if (finalWidth > (Number(d.width) || 0)) {
      d.width = Number(finalWidth.toFixed(2));
      bumped = { id: d.id, room_name: roomName, target: d.width };
    }
    break;
  }
  return { blueprint: out, bumped, blocked, target };
}

/**
 * Audit-only: returns `{ violations: [{door_id, room_name, current, target}] }`
 * without mutating the blueprint. Used by the inspector's "audit" mode.
 */
export function auditAdaCompliance(blueprint) {
  if (!blueprint) return { violations: [] };
  const out = [];
  for (const floor of blueprint.floors || []) {
    if (!floor || !Array.isArray(floor.doors)) continue;
    for (const d of floor.doors) {
      if (!d || d.deleted) continue;
      const roomName = _findHostRoomName(floor, d);
      const target = adaDoorTargetWidth(roomName);
      const current = Number.isFinite(d.width) ? Number(d.width) : 0.9;
      if (current < target - 0.005) {
        out.push({
          door_id: d.id || null,
          room_name: roomName,
          current,
          target,
        });
      }
    }
  }
  return { violations: out };
}
